"use client";
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { DevaKathaLS2 } from "./DevaKathaLS2";
import { DevaKathaLS5 } from "./DevaKathaLS5";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Nirjala Ekadashi phrase overlays — Trimbakeshwar holes treatment. Top-left, yellow-prominent,
// per-word colour + line breaks, black drop-shadow, scattered-holes mask, word-by-word drop-in.
// Times ABSOLUTE seconds on the assembled narration (jyotirlinga — no opener shift). 1280×704 @ 16fps.
// Every start anchored to the exact word in word_timestamps_hindi (no estimation).
const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";
const HOLES = HOLES_DATA_URI;

const BASE_FS = 66, BIG_FS = 92, SMALL_FS = 46;
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -10;

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; lineGap?: number; lh?: number; ls?: string };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

// 24 selective phrases (Yash-approved 2026-06-20, cut 8/9/10/11/13/21/25/28/33 from the draft).
// Latin numerals (24); dharma/name terms in Devanagari. Anchored to word_timestamps_hindi.
export const NIRJALA_PHRASES: Phrase[] = [
  { start: 0.0,   out: 3.4,   lines: [[{ t: "निर्जला", c: "y" }, { t: "एकादशी", c: "w", big: true }]] },
  { start: 4.5,   out: 7.9,   lines: [[{ t: "बिना", c: "r", big: true }, { t: "पानी", c: "w" }]] },
  { start: 18.7,  out: 22.1,  lines: [[{ t: "24", c: "w", big: true }, { t: "घंटे", c: "y" }]] },
  { start: 25.7,  out: 29.1,  lines: [[{ t: "महाबली", c: "y" }, { t: "भीम", c: "w", big: true }]] },
  { start: 31.5,  out: 34.9,  lines: [[{ t: "वृकोदर", c: "r", big: true }]] },
  { start: 40.2,  out: 43.6,  lines: [[{ t: "24", c: "w", big: true }, { t: "एकादशी", c: "y" }]] },
  { start: 43.9,  out: 47.3,  lines: [[{ t: "धधकती", c: "r", big: true }, { t: "भूख", c: "w" }]] },
  { start: 111.3, out: 114.7, lines: [[{ t: "ज्येष्ठ", c: "w", big: true }, { t: "मास", c: "y" }]] },
  { start: 125.1, out: 128.5, lines: [[{ t: "झुलसा", c: "y" }, { t: "देती", small: true }], [{ t: "गर्मी", c: "r", big: true }]] },
  { start: 140.0, out: 143.4, lines: [[{ t: "चुनौती", c: "y" }, { t: "स्वीकार", c: "w", big: true }]] },
  { start: 145.1, out: 148.5, lines: [[{ t: "असंभव", c: "r", big: true }, { t: "व्रत", c: "w" }]] },
  { start: 180.9, out: 184.3, lines: [[{ t: "बड़ा", c: "y" }, { t: "विरोधाभास", c: "r", big: true }]] },
  { start: 197.2, out: 200.6, lines: [[{ t: "मत्स्य", c: "y" }, { t: "पुराण", c: "w", big: true }]] },
  { start: 203.2, out: 206.6, lines: [[{ t: "भीम", c: "y" }, { t: "द्वादशी", c: "w", big: true }]] },
  { start: 210.8, out: 214.2, lines: [[{ t: "माघ", c: "w", big: true }, { t: "मास", c: "y" }]] },
  { start: 265.3, out: 268.7, lines: [[{ t: "खगोल", c: "y" }, { t: "विज्ञान", c: "w", big: true }]] },
  { start: 271.1, out: 274.5, lines: [[{ t: "शुक्ल", c: "y" }, { t: "पक्ष", c: "w", big: true }]] },
  { start: 289.7, out: 293.1, lines: [[{ t: "24", c: "w", big: true }, { t: "एकादशी", c: "y" }]] },
  { start: 395.7, out: 399.1, lines: [[{ t: "ऑटोफैगी", c: "w", big: true }]] },
  { start: 415.6, out: 419.0, lines: [[{ t: "योशिनोरी", c: "y" }], [{ t: "ओसुमी", c: "w", big: true }]] },
  { start: 450.6, out: 454.0, lines: [[{ t: "शरीर", c: "y" }, { t: "रीसेट", c: "r", big: true }]] },
  { start: 477.8, out: 481.2, lines: [[{ t: "पी.", c: "y" }, { t: "वी.", c: "y" }, { t: "काणे", c: "w", big: true }]] },
  { start: 485.2, out: 488.6, lines: [[{ t: "गीता", c: "y" }, { t: "प्रेस", c: "w", big: true }]] },
  { start: 534.7, out: 538.1, lines: [[{ t: "तेरा", c: "y" }, { t: "निर्जला", c: "w", big: true }]] },
];

const PhraseBlock: React.FC<{ p: Phrase; fps: number }> = ({ p, fps }) => {
  const frame = useCurrentFrame();
  const t = frame / fps;
  const phraseOp = interpolate(t, [p.out - 0.4, p.out], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (t < p.start - 0.1 || t > p.out + 0.1) return null;
  const gap = p.lineGap ?? LINE_GAP;
  const lh = p.lh ?? LH;
  let gi = -1;
  return (
    <div style={{ position: "absolute", top: PAD, left: PAD, fontFamily, opacity: phraseOp, display: "flex", flexDirection: "column" }}>
      {p.lines.map((line, li) => (
        <div key={li} style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "0 16px", marginTop: li === 0 ? 0 : gap }}>
          {line.map((w, wi) => {
            gi += 1;
            const aT = p.start + gi * STAGGER;
            const k = Math.min(Math.max((t - aT) / DUR, 0), 1);
            const ease = 1 - Math.pow(1 - k, 3);
            const op = interpolate(t, [aT, aT + 0.18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const y = -DROP * (1 - ease);
            return (
              <span key={wi} style={{
                display: "inline-block",
                transform: `translateY(${y}px)`,
                opacity: op,
                color: col(w.c),
                fontSize: w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS,
                fontWeight: w.big ? 900 : 800,
                lineHeight: lh,
                letterSpacing: p.ls ?? "0.005em",
                textShadow: SH,
              }}>{w.t}</span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Bottom-RIGHT viewing tip — two lines, yellow "Watch at 1.2x" + smaller white "for best experience".
// NO holes mask. ~0.5→5.5s only.
const WatchTag: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.4, out - 0.4, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, right: PAD, fontFamily, opacity: op,
                  display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right" }}>
      <span style={{ color: YELLOW, fontSize: SMALL_FS, fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        Watch at 1.2x
      </span>
      <span style={{ color: WHITE, fontSize: Math.round(SMALL_FS * 0.7), fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        for best experience
      </span>
    </div>
  );
};

// Master comp — holes-masked devotional phrases + non-masked UI (watch tip + LS pills).
// आगे देखिए teaser + WhatsApp pill intentionally OMITTED (Yash 2026-06-20). Logo added by Modal entrypoint.
export const NirjalaOverlaysFull: React.FC<{ phrases?: Phrase[] }> = ({ phrases = NIRJALA_PHRASES }) => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      {/* holes distress mask over the dramatic top-left phrases only */}
      <AbsoluteFill style={{
        backgroundColor: "transparent",
        WebkitMaskImage: `url(${HOLES})`, maskImage: `url(${HOLES})`,
        WebkitMaskSize: "1280px 704px", maskSize: "1280px 704px",
        WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
        WebkitMaskPosition: "0 0", maskPosition: "0 0",
      }}>
        {phrases.map((p, i) => <PhraseBlock key={i} p={p} fps={fps} />)}
      </AbsoluteFill>

      {/* viewing tip (bottom-right) */}
      <WatchTag fps={fps} start={0.5} out={5.5} />

      {/* Like+Subscribe — LS2 (bell) @ "सब्सक्राइब" 165.3s · LS5 (no bell) @ outro "पूछें" 531.2s */}
      <Sequence from={S(165.3)} durationInFrames={S(5.2)}><DevaKathaLS2 /></Sequence>
      <Sequence from={S(531.2)} durationInFrames={S(5.2)}><DevaKathaLS5 /></Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
